/* Homepage 02 "Waarom het werkt" (03-11-2026, R8). One benefit stage with eight swipe cards:
   native scroll-snap with the next card peeking in, text pills on top and a control row under
   the cards (previous, progress, "Volgende: <next card>"); no auto-advance (NN/g: auto-forwarding
   carousels lower visibility, most of all on mobile). When a card's picture has played, the next
   button pulses and turns solid; a tap on the peeking card or a progress segment opens it. Cards build up through .is-active.
   Card 1 shows twenty players without and with Nordic curls side by side. Cards 2 and 4 pose one
   athlete figure (.nbev__rig): on card 3 it falls over alone, is held by a partner, then by the
   door and Nordbelt (looping while shown); card 5 loops the Nordic curl while the hamstring
   lights up as it brakes. Card 2 compares two seasons of professional football as rings.
   Source numbers open the sources. */
(() => {
  const root = document.querySelector('[data-nbev]');
  if (!root || root.dataset.nbevReady) return;
  root.dataset.nbevReady = '1';
  root.classList.add('nbev--js');

  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const deck = root.querySelector('[data-nbev-deck]');
  const track = deck && deck.querySelector('[data-nbev-track]');
  const clamp = (x) => Math.max(0, Math.min(1, x));
  const smooth = (x) => { const k = clamp(x); return k * k * (3 - 2 * k); };
  let ready = () => {};

  /* ---------- Card 1: twenty players, without and with ----------
     Positions are 1-based. All players: 3 → 1 in 20 (15%, ~70% fewer). Injured before:
     6 → 1 in 20 (28%, 86% fewer recurrences). */
  const MODES = {
    all: { without: [4, 12, 17], hurt: [12], saved: [4, 17] },
    prior: { without: [2, 4, 9, 12, 15, 17], hurt: [12], saved: [2, 4, 9, 15, 17] },
  };
  const team = root.querySelector('[data-nbev-team]');
  const lines = team ? [...team.querySelectorAll('.nbev__line')] : [];
  const rows = lines.map((l) => [...l.querySelectorAll('.nbev__person')]);
  const base = team && team.querySelector('[data-nbev-base]');
  const count = team && team.querySelector('[data-nbev-count]');
  const gain = team && team.querySelector('.nbev__gain');
  let teamTimer = 0;
  let countTimers = [];

  const setCount = (n) => {
    if (count && count.dataset.fmt) count.textContent = count.dataset.fmt.replace('#', n);
  };

  // The "with" count ticks down in step with the players turning ice (CSS: --d × 0.16 s).
  const tickCount = (from, to) => {
    countTimers.forEach(clearTimeout);
    countTimers = [];
    setCount(from);
    for (let k = 1; k <= from - to; k += 1) {
      countTimers.push(setTimeout(() => setCount(from - k), reduce ? 0 : 260 + k * 160));
    }
  };

  const renderTeam = (state) => {
    const mode = team.dataset.mode;
    const m = MODES[mode];
    team.dataset.state = state;
    rows[0].forEach((p, i) => p.classList.toggle('is-hurt', m.without.includes(i + 1)));
    let d = 0;
    rows[1].forEach((p, i) => {
      const n = i + 1;
      const saved = state === 'with' && m.saved.includes(n);
      p.classList.toggle('is-hurt', state === 'with' ? m.hurt.includes(n) || saved : m.without.includes(n));
      p.classList.toggle('is-saved', saved);
      p.style.setProperty('--d', saved ? String(d++) : '0');
    });
    if (base) base.textContent = base.dataset[mode + 'Without'];
    if (state === 'with') tickCount(m.without.length, m.hurt.length);
    else tickCount(m.without.length, m.without.length);
    if (gain) gain.textContent = gain.dataset[mode];
  };

  const playTeam = () => {
    if (!team) return;
    clearTimeout(teamTimer);
    team.classList.add('is-reset');
    renderTeam('without');
    void team.offsetWidth;
    team.classList.remove('is-reset');
    teamTimer = setTimeout(() => renderTeam('with'), reduce ? 0 : 1100);
  };

  if (team) {
    team.querySelectorAll('[data-nbev-mode]').forEach((b) => b.addEventListener('click', () => {
      const mode = b.dataset.nbevMode;
      if (team.dataset.mode === mode) return;
      team.dataset.mode = mode;
      team.querySelectorAll('[data-nbev-mode]').forEach((x) => x.setAttribute('aria-pressed', x === b ? 'true' : 'false'));
      playTeam();
    }));
  }

  /* ---------- The athlete (cards 3 and 5) ----------
     Proportions and motion of the earlier Nordic curl animation (Steel's canvas), redrawn as
     SVG in the section's colours. Units are the old canvas units: knee at (270, 448) on the
     floor. o = thigh angle from the floor (100° kneeling tall, 14° nearly flat); the torso
     bends at the hip towards the bottom; arms swing and the hands stop at the floor; a small
     wobble keeps it alive. lift tips the shins up (falling over without anchor), glow (0–1)
     lights the hamstring. */
  const RAD = Math.PI / 180;
  const n1 = (v) => Math.round(v * 10) / 10;
  const KX = 270;
  const FLOOR = 448;
  const UP = 100 * RAD;
  const LOW = 14 * RAD;
  const SPAN = UP - LOW;
  const BEND = UP - 87 * RAD;
  const FLAT = -1 * RAD;
  const WOBBLE = { thigh: [1.7, 0, .01], torso: [2.3, 1.1, .014], head: [3.1, 2.5, .02], arm: [2, .7, .012] };
  const wob = (k, t) => Math.sin(t * WOBBLE[k][0] * 2 * Math.PI + WOBBLE[k][1]) * WOBBLE[k][2];
  const bendAt = (o) => { const u = clamp(1 - (o - LOW) / SPAN); return BEND + u * u * (LOW - FLAT - BEND); };
  const ANKLE = Math.PI - Math.asin(34 / 158);

  const makeFigure = (g) => {
    if (!g) return null;
    const f = {};
    g.querySelectorAll('[data-f]').forEach((el) => { f[el.dataset.f] = el; });
    const line = (el, x1, y1, x2, y2) => {
      if (!el) return;
      el.setAttribute('x1', n1(x1));
      el.setAttribute('y1', n1(y1));
      el.setAttribute('x2', n1(x2));
      el.setAttribute('y2', n1(y2));
    };
    // A thin light stripe along a limb, a little off its axis, as in the canvas version.
    const shine = (el, x1, y1, x2, y2, w) => {
      const dx = x2 - x1; const dy = y2 - y1; const l = Math.hypot(dx, dy) || 1;
      const ox = (-dy / l) * w * .22; const oy = (dx / l) * w * .22;
      line(el, x1 + ox, y1 + oy, x2 + ox, y2 + oy);
    };
    return (o, t = 0, lift = 0, glow = 0) => {
      const th = o + wob('thigh', t);
      const tw = th - (bendAt(th) + wob('torso', t));
      const aa = ANKLE - lift;
      const ax = KX + Math.cos(aa) * 158; const ay = FLOOR - Math.sin(aa) * 158;
      // Toes on the floor behind the ankle; they tip up with the shins.
      const fx = ax - 26 * Math.cos(lift) + 30 * Math.sin(lift); const fy = ay + 30 * Math.cos(lift) + 26 * Math.sin(lift);
      const hx = KX + Math.cos(th) * 152; const hy = FLOOR - Math.sin(th) * 152;
      const sx = hx + Math.cos(tw) * 175; const sy = hy - Math.sin(tw) * 175;
      const px = sx + .2 * (hx - sx); const py = sy + .2 * (hy - sy);
      const A = tw + Math.PI + 25 * RAD + wob('arm', t);
      const ex = px + Math.cos(A) * 92; const ey = Math.min(FLOOR, py - Math.sin(A) * 92);
      const P = A + 170 * RAD + clamp(tw / (Math.PI / 2)) * (-80 * RAD);
      const wx = ex + Math.cos(P) * 63; const wy = Math.min(FLOOR - 4, ey - Math.sin(P) * 63);
      const ha = tw + wob('head', t);
      const cx = sx + Math.cos(ha) * 31; const cy = sy - Math.sin(ha) * 31;
      line(f.glow1, KX, FLOOR, hx, hy);
      line(f.glow2, KX, FLOOR, hx, hy);
      line(f.glow3, KX, FLOOR, hx, hy);
      line(f.shin, KX, FLOOR, ax, ay);
      line(f.foot, ax, ay, fx, fy);
      line(f.thigh, KX, FLOOR, hx, hy);
      line(f.torso, hx, hy, sx, sy);
      line(f.upper, px, py, ex, ey);
      line(f.fore, ex, ey, wx, wy);
      shine(f.hlShin, KX, FLOOR, ax, ay, 21);
      shine(f.hlThigh, KX, FLOOR, hx, hy, 25);
      shine(f.hlTorso, hx, hy, sx, sy, 30);
      if (f.head) {
        f.head.setAttribute('cx', n1(cx));
        f.head.setAttribute('cy', n1(cy));
      }
      if (f.hair) {
        // Hair over the top and back of the head.
        const c = ha + 45 * RAD; const r = 26;
        const p1 = c - 80 * RAD; const p2 = c + 80 * RAD;
        f.hair.setAttribute('d', `M${n1(cx + Math.cos(p1) * r)} ${n1(cy - Math.sin(p1) * r)}A${r} ${r} 0 0 0 ${n1(cx + Math.cos(p2) * r)} ${n1(cy - Math.sin(p2) * r)}`);
      }
      if (f.shadow) {
        const lo = Math.min(fx, ax) - 20; const hi = Math.max(cx + 31, wx, KX + 40) + 20;
        f.shadow.setAttribute('cx', n1((lo + hi) / 2));
        f.shadow.setAttribute('rx', n1((hi - lo) / 2));
      }
      g.style.setProperty('--glow', glow.toFixed(3));
      return { ax, ay };
    };
  };

  /* ---------- Card 3: alone you fall over, a partner holds you, then door and Nordbelt ----------
     Beats in ms: alone 0–2600 (lean, the shins come up, you fall onto your hands), partner
     2600–4900, door and Nordbelt 4900–8800 (a full controlled rep with the hamstring lit),
     then again while the card is shown. Tap the picture to replay from the start. */
  const solo = root.querySelector('[data-nbev-solo]');
  const soloFig = solo && solo.querySelector('[data-nbev-fig]');
  const soloTip = solo && solo.querySelector('.nbev__sc-tip');
  const soloPose = makeFigure(soloFig);
  const SOLO = 8800;
  let soloRaf = 0;
  let soloOn = false;
  const soloAt = (t) => {
    if (t < 2600) {
      const k = clamp((t - 300) / 1350);
      const o = UP - SPAN * Math.pow(k, 2.2);
      const lift = clamp((72 * RAD - o) / (58 * RAD)) * 40 * RAD;
      return { scene: 'alone', o, lift, glow: 0, fade: t < 2300 ? 1 : 0 };
    }
    if (t < 4900) {
      const o = UP - 52 * RAD * smooth((t - 2950) / 1050) + 52 * RAD * smooth((t - 4150) / 600);
      return { scene: 'partner', o, lift: 0, glow: 0, fade: 1 };
    }
    const down = smooth((t - 5400) / 1700);
    const up = smooth((t - 7650) / 750);
    const o = UP - 66 * RAD * down + 66 * RAD * up;
    return { scene: 'solo', o, lift: 0, glow: .95 * down * (1 - up), fade: t < SOLO - 280 ? 1 : 0 };
  };
  const playSolo = () => {
    if (!solo || !soloPose) return;
    cancelAnimationFrame(soloRaf);
    soloOn = true;
    if (reduce) {
      solo.dataset.scene = 'solo';
      soloPose(52 * RAD, 0, 0, .7);
      ready(solo);
      return;
    }
    let t0 = 0;
    const frame = (now) => {
      if (!t0) t0 = now;
      const t = now - t0;
      const s = soloAt(t);
      if (solo.dataset.scene !== s.scene) solo.dataset.scene = s.scene;
      solo.classList.toggle('is-tip', s.lift > .05);
      soloFig.style.opacity = String(s.fade);
      const at = soloPose(s.o, t / 1000, s.lift, s.glow);
      if (soloTip) soloTip.setAttribute('transform', `translate(${n1(at.ax - 116)} ${n1(at.ay - 414)})`);
      if (t > 7200) ready(solo);
      if (t >= SOLO) t0 = now; // play the story again
      if (soloOn) soloRaf = requestAnimationFrame(frame);
    };
    soloRaf = requestAnimationFrame(frame);
  };
  const stopSolo = () => {
    soloOn = false;
    cancelAnimationFrame(soloRaf);
  };
  if (solo) {
    const viz = solo.querySelector('.nbev__scene');
    if (viz) viz.addEventListener('click', () => { if (solo.classList.contains('is-active')) playSolo(); });
  }

  /* ---------- Card 5: the Nordic curl at the door ----------
     The old loop: 6.4 s per rep; set up (30%), lower under control while the hamstring lights
     up and the motion meter fills, hands to the floor, back up. Reps count up in the ring.
     Runs only while the card is shown. */
  const how = root.querySelector('[data-nbev-how]');
  const howPose = makeFigure(how && how.querySelector('[data-nbev-fig]'));
  const meter = how && how.querySelector('[data-nbev-meter]');
  const pct = how && how.querySelector('[data-nbev-pct]');
  const reps = how && how.querySelector('[data-nbev-reps]');
  const repsArc = how && how.querySelector('[data-nbev-reps-arc]');
  const chips = how ? [...how.querySelectorAll('[data-phase]')] : [];
  const texts = how ? [...how.querySelectorAll('[data-phase-text]')] : [];
  const CURL = 6400;
  const SET = .3;
  const DOWN = .75;
  const curlAt = (e) => {
    if (e < SET) return { o: UP, glow: 0, phase: 0, move: 0 };
    const m = (e - SET) / (1 - SET);
    if (m < DOWN) {
      const k = m / DOWN;
      // Slow at first, faster near the floor where the hamstrings can no longer hold.
      const o = UP - SPAN * (.55 * Math.pow(k, 1.8) + .45 * (1 - Math.pow(1 - k, 2.4)));
      const n = k;
      const glow = Math.min(1, 3.9 * n) * (1 - Math.pow(Math.max(0, n - .9) / .1, 2));
      return { o, glow, phase: e < SET + .06 ? 0 : 1, move: m };
    }
    const k = (m - DOWN) / (1 - DOWN);
    const x = clamp(k);
    return { o: LOW + SPAN * x * x * x * (x * (6 * x - 15) + 10), glow: 0, phase: 2, move: m };
  };
  let howRaf = 0;
  let howStart = 0;
  let howPhase = -1;
  let howRep = -1;
  const setPhase = (k) => {
    if (k === howPhase) return;
    howPhase = k;
    chips.forEach((c, i) => c.classList.toggle('is-active', i === k));
    texts.forEach((c, i) => c.classList.toggle('is-active', i === k));
  };
  const howFrame = (now) => {
    if (!howStart) howStart = now;
    const ms = now - howStart;
    const s = curlAt((ms % CURL) / CURL);
    const pulse = .84 + .34 * (.5 + .5 * Math.sin(ms / 1000 * 6.2));
    howPose(s.o, ms / 1000, 0, s.glow * pulse);
    const load = clamp((UP - s.o) / SPAN);
    const p = Math.round(20 + 80 * load);
    if (meter) meter.style.setProperty('--load', (p / 100).toFixed(3));
    if (pct) pct.textContent = (pct.dataset.fmt || '#%').replace('#', p);
    const rep = Math.floor(ms / CURL);
    if (reps && rep !== howRep) { howRep = rep; reps.textContent = String(rep + 1); }
    if (repsArc) repsArc.style.setProperty('--rep', (clamp(s.move) * 100).toFixed(1));
    setPhase(s.phase);
    if (ms > CURL * .92) ready(how);
    howRaf = requestAnimationFrame(howFrame);
  };
  const runHow = (on) => {
    if (!howPose) return;
    if (reduce) {
      howPose(48 * RAD, 0, 0, .8);
      return;
    }
    if (on && !howRaf) {
      howStart = 0;
      howRep = -1;
      howRaf = requestAnimationFrame(howFrame);
    } else if (!on && howRaf) {
      cancelAnimationFrame(howRaf);
      howRaf = 0;
    }
  };
  // First paint before anything plays: card 3 mid-rep with Nordbelt, card 5 halfway down.
  if (soloPose) soloPose(52 * RAD, 0, 0, .7);
  if (howPose) howPose(52 * RAD, 0, 0, .8);

  /* ---------- Stage ---------- */
  if (!deck || !track) return;
  const slides = [...track.children];
  const pills = [...deck.querySelectorAll('[data-go]')];
  const nav = deck.querySelector('[data-nbev-nav]');
  const prev = deck.querySelector('[data-nbev-prev]');
  const next = deck.querySelector('[data-nbev-next]');
  const nextName = next && next.querySelector('[data-nbev-next-name]');
  const segs = [...deck.querySelectorAll('.nbev__segs i')];
  const last = slides.length - 1;
  const seen = new Set([0]);
  let index = 0;
  let settle = 0;
  let readyTimer = 0;
  let inView = false;

  // The next button pulses once the active card has played (cards 2 and 4 say so themselves).
  ready = (slide) => {
    if (next && slides[index] === slide && !next.classList.contains('is-ready')) next.classList.add('is-ready');
  };

  const step = () => (slides[1] ? slides[1].offsetLeft - slides[0].offsetLeft : track.clientWidth) || 1;

  const setActive = (i, force) => {
    if (i === index && !force) return;
    index = i;
    seen.add(i);
    slides.forEach((s, k) => s.classList.toggle('is-active', k === i));
    pills.forEach((p, k) => {
      if (k === i) p.setAttribute('aria-current', 'true');
      else p.removeAttribute('aria-current');
      p.classList.toggle('is-seen', seen.has(k));
    });
    segs.forEach((s, k) => {
      s.classList.toggle('is-current', k === i);
      s.classList.toggle('is-seen', seen.has(k));
    });
    if (prev) prev.disabled = i === 0;
    if (next) {
      next.classList.remove('is-ready');
      next.classList.toggle('is-again', i === last);
      if (nextName) nextName.textContent = i === last ? next.dataset.again : pills[i + 1].dataset.label;
    }
    if (nav && nav.scrollWidth > nav.clientWidth + 2) {
      const p = pills[i];
      nav.scrollTo({ left: p.offsetLeft - (nav.clientWidth - p.offsetWidth) / 2, behavior: reduce ? 'auto' : 'smooth' });
    }
    clearTimeout(readyTimer);
    const slide = slides[i];
    if (slide === team) playTeam();
    if (slide === solo && inView) playSolo();
    else stopSolo();
    runHow(slide === how && inView);
    if (slide !== solo && slide !== how) readyTimer = setTimeout(() => ready(slide), slide === team ? 3000 : 2400);
  };

  const go = (i) => {
    const n = Math.max(0, Math.min(last, i));
    track.scrollTo({ left: n * step(), behavior: reduce ? 'auto' : 'smooth' });
    setActive(n);
  };

  track.addEventListener('scroll', () => {
    clearTimeout(settle);
    settle = setTimeout(() => setActive(Math.round(track.scrollLeft / step())), 90);
  }, { passive: true });
  pills.forEach((p, k) => p.addEventListener('click', () => go(k)));
  segs.forEach((g, k) => g.addEventListener('click', () => go(k)));
  if (prev) prev.addEventListener('click', () => go(index - 1));
  if (next) next.addEventListener('click', () => go(index === last ? 0 : index + 1));

  // A tap on the card that peeks in opens it.
  track.addEventListener('click', (e) => {
    const s = e.target.closest('.nbev__slide');
    if (!s || s.classList.contains('is-active') || e.target.closest('a, button')) return;
    go(slides.indexOf(s));
  });

  deck.addEventListener('keydown', (e) => {
    if (e.target.closest('.nbev__modes')) return;
    if (e.key === 'ArrowRight') { e.preventDefault(); go(index + 1); }
    if (e.key === 'ArrowLeft') { e.preventDefault(); go(index - 1); }
  });

  // First time in view: play card 1 and nudge the cards once so the swipe is obvious.
  let started = false;
  const first = () => {
    setActive(0, true);
    if (reduce || track.scrollWidth <= track.clientWidth + 4) return;
    setTimeout(() => {
      if (track.scrollLeft > 4) return;
      track.classList.add('is-nudge');
      setTimeout(() => track.classList.remove('is-nudge'), 1300);
    }, 2600);
  };
  if (!('IntersectionObserver' in window)) {
    inView = true;
    first();
  } else {
    const io = new IntersectionObserver((entries) => {
      const e = entries[entries.length - 1];
      inView = e.intersectionRatio >= .3;
      if (!started && e.intersectionRatio >= .45) {
        started = true;
        first();
      }
      runHow(inView && slides[index] === how);
      if (!inView) stopSolo();
      else if (slides[index] === solo && !soloOn) playSolo();
    }, { threshold: [0, .3, .45] });
    io.observe(deck);
  }

  /* ---------- Source numbers ---------- */
  const sources = root.querySelector('[data-nbev-src]');
  root.addEventListener('click', (e) => {
    const link = e.target.closest('[data-nbev-ref]');
    if (!link || !sources) return;
    const item = document.getElementById(link.hash.slice(1));
    if (!item) return;
    e.preventDefault();
    sources.open = true;
    item.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
    item.classList.add('is-flash');
    setTimeout(() => item.classList.remove('is-flash'), 1600);
  });
})();
